import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Company, CompanyUser, CreateCompanyData, CompanyFilters, CompanyUserFilters, Property } from '../../types';
import { companyService } from '../../services/companyService';
import cookieService from '../../services/cookieService';
import { logout } from './authSlice';

interface CompaniesState {
  companies: Company[];
  currentCompany: Company | null;
  companyUsers: CompanyUser[];
  companyProperties: Property[];
  companyStats: {
    totalProperties: number;
    totalRentalUnits: number;
    totalUsers: number;
    occupiedUnits: number;
    availableUnits: number;
    occupancyRate: string;
    totalRevenue: number;
  } | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters: CompanyFilters;
  userFilters: CompanyUserFilters;
  isLoading: boolean;
  isLoadingCompanies: boolean; // Specific flag for companies fetch
  companiesLoaded: boolean; // Track if companies have been loaded
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

const initialState: CompaniesState = {
  companies: [],
  currentCompany: null,
  companyUsers: [],
  companyProperties: [],
  companyStats: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  filters: {
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  userFilters: {
    page: 1,
    limit: 10
  },
  isLoading: false,
  isLoadingCompanies: false,
  companiesLoaded: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null
};

// Async thunks
export const fetchCompanies = createAsyncThunk(
  'companies/fetchCompanies',
  async (filters: CompanyFilters | undefined, { getState }) => {
    const state = getState() as { companies: CompaniesState };

    // Skip if companies are already loaded (use cached data)
    if (state.companies.companiesLoaded && !filters) {
      return { data: state.companies.companies, pagination: state.companies.pagination };
    }

    const response = await companyService.getCompanies(filters);
    return response;
  },
  {
    // Use condition to prevent duplicate requests
    condition: (_, { getState }) => {
      const state = getState() as { companies: CompaniesState };
      // Don't fetch if already loading
      if (state.companies.isLoadingCompanies) {
        console.log('Skipping fetchCompanies - already loading');
        return false;
      }
      return true;
    }
  }
);

export const fetchCompany = createAsyncThunk(
  'companies/fetchCompany',
  async (id: string) => {
    const response = await companyService.getCompany(id);
    return response.data;
  }
);

export const createCompany = createAsyncThunk(
  'companies/createCompany',
  async (data: CreateCompanyData, { rejectWithValue }) => {
    try {
      const response = await companyService.createCompany(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCompany = createAsyncThunk(
  'companies/updateCompany',
  async ({ id, data }: { id: string; data: Partial<CreateCompanyData> }) => {
    const response = await companyService.updateCompany(id, data);
    return response.data;
  }
);

export const deleteCompany = createAsyncThunk(
  'companies/deleteCompany',
  async (id: string) => {
    await companyService.deleteCompany(id);
    return id;
  }
);

export const verifyCompany = createAsyncThunk(
  'companies/verifyCompany',
  async ({ id, isVerified, notes }: { id: string; isVerified: boolean; notes?: string }) => {
    const response = await companyService.verifyCompany(id, isVerified, notes);
    return response.data;
  }
);

export const fetchCompanyStats = createAsyncThunk(
  'companies/fetchCompanyStats',
  async (id: string) => {
    const response = await companyService.getCompanyStats(id);
    return response.data;
  }
);

export const fetchCompanyUsers = createAsyncThunk(
  'companies/fetchCompanyUsers',
  async ({ companyId, filters }: { companyId: string; filters?: CompanyUserFilters }) => {
    const response = await companyService.getCompanyUsers(companyId, filters);
    return response.data;
  }
);

export const addUserToCompany = createAsyncThunk(
  'companies/addUserToCompany',
  async ({ companyId, data }: {
    companyId: string;
    data: {
      userId: string;
      role: string;
      permissions?: any[];
      designation?: string;
      department?: string;
    }
  }) => {
    const response = await companyService.addUserToCompany(companyId, data);
    return response.data;
  }
);

export const updateCompanyUser = createAsyncThunk(
  'companies/updateCompanyUser',
  async ({ companyId, userId, data }: {
    companyId: string;
    userId: string;
    data: Partial<{
      role: string;
      permissions: any[];
      designation: string;
      department: string;
      isActive: boolean;
    }>
  }) => {
    const response = await companyService.updateCompanyUser(companyId, userId, data);
    return response.data;
  }
);

export const removeUserFromCompany = createAsyncThunk(
  'companies/removeUserFromCompany',
  async ({ companyId, userId }: { companyId: string; userId: string }) => {
    await companyService.removeUserFromCompany(companyId, userId);
    return userId;
  }
);

export const fetchCompanyProperties = createAsyncThunk(
  'companies/fetchCompanyProperties',
  async (companyId: string) => {
    const response = await companyService.getCompanyProperties(companyId);
    return response.data;
  }
);

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<CompanyFilters>) => {
      state.filters = action.payload;
    },
    setUserFilters: (state, action: PayloadAction<CompanyUserFilters>) => {
      state.userFilters = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCompanies: (state) => {
      state.companies = [];
      state.currentCompany = null;
      state.companyUsers = [];
      state.companyStats = null;
      state.pagination = initialState.pagination;
      state.companiesLoaded = false;
      state.isLoadingCompanies = false;
    },
    setCurrentCompany: (state, action: PayloadAction<Company | null>) => {
      state.currentCompany = action.payload;
      // Save to cookie when manually setting current company
      if (action.payload) {
        cookieService.set('currentCompanyId', action.payload._id, { expires: 7 });
      } else {
        // Clear cookie if setting to null
        cookieService.delete('currentCompanyId');
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch companies
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.isLoading = true;
        state.isLoadingCompanies = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingCompanies = false;
        state.companiesLoaded = true;
        state.companies = action.payload.data;

        // Auto-select company from cookie or first available
        if (!state.currentCompany && action.payload.data.length > 0) {
          // Check if there's a saved company ID in cookie
          const savedCompanyId = cookieService.get('currentCompanyId');

          if (savedCompanyId) {
            // Find the saved company in the list
            const savedCompany = action.payload.data.find((c: Company) => c._id === savedCompanyId);
            if (savedCompany) {
              state.currentCompany = savedCompany;
              // Refresh the cookie expiry to extend it by 7 more days
              cookieService.set('currentCompanyId', savedCompanyId, { expires: 7 });
            } else {
              // Saved company not found in list, select first available
              state.currentCompany = action.payload.data[0];
              cookieService.set('currentCompanyId', action.payload.data[0]._id, { expires: 7 });
            }
          } else {
            // No saved company in cookie, select first available
            state.currentCompany = action.payload.data[0];
            cookieService.set('currentCompanyId', action.payload.data[0]._id, { expires: 7 });
          }
        }

        if (action.payload.pagination) {
          const pagination = action.payload.pagination as any;
          state.pagination = {
            page: pagination.page || 1,
            limit: pagination.limit || 10,
            total: pagination.totalCount || pagination.total || 0,
            pages: pagination.totalPages || pagination.pages || 0
          };
        }
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        // Only update state if it's not a cancelled action (from condition check)
        if (action.meta.condition === false) {
          return; // Request was cancelled due to condition
        }
        state.isLoading = false;
        state.isLoadingCompanies = false;
        state.error = action.error.message || 'Failed to fetch companies';
      })
      
      // Fetch single company
      .addCase(fetchCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.currentCompany = action.payload || null;
      })
      .addCase(fetchCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch company';
      })
      
      // Create company
      .addCase(createCompany.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.companies.unshift(action.payload);
        }
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create company';
      })
      
      // Update company
      .addCase(updateCompany.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const index = state.companies.findIndex(c => c._id === action.payload!._id);
          if (index !== -1) {
            state.companies[index] = action.payload;
          }
          if (state.currentCompany?._id === action.payload._id) {
            state.currentCompany = action.payload;
          }
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.error.message || 'Failed to update company';
      })
      
      // Delete company
      .addCase(deleteCompany.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.companies = state.companies.filter(c => c._id !== action.payload);
        if (state.currentCompany?._id === action.payload) {
          state.currentCompany = null;
        }
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.error.message || 'Failed to delete company';
      })
      
      // Verify company
      .addCase(verifyCompany.fulfilled, (state, action) => {
        const company = action.payload;
        if (company) {
          const index = state.companies.findIndex(c => c._id === company._id);
          if (index !== -1) {
            state.companies[index] = company;
          }
          if (state.currentCompany?._id === company._id) {
            state.currentCompany = company;
          }
        }
      })
      
      // Fetch company stats
      .addCase(fetchCompanyStats.fulfilled, (state, action) => {
        if (action.payload) {
          state.companyStats = action.payload;
        }
      })
      .addCase(fetchCompanyStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch company stats';
      })
      
      // Fetch company users
      .addCase(fetchCompanyUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.companyUsers = action.payload;
        }
      })
      .addCase(fetchCompanyUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch company users';
      })
      
      // Add user to company
      .addCase(addUserToCompany.fulfilled, (state, action) => {
        if (action.payload) {
          state.companyUsers.push(action.payload);
        }
      })
      .addCase(addUserToCompany.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add user to company';
      })
      
      // Update company user
      .addCase(updateCompanyUser.fulfilled, (state, action) => {
        const user = action.payload;
        if (user) {
          const index = state.companyUsers.findIndex(u => u._id === user._id);
          if (index !== -1) {
            state.companyUsers[index] = user;
          }
        }
      })
      .addCase(updateCompanyUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update company user';
      })
      
      // Remove user from company
      .addCase(removeUserFromCompany.fulfilled, (state, action) => {
        if (action.payload) {
          state.companyUsers = state.companyUsers.filter(u => u.userId !== action.payload);
        }
      })
      .addCase(removeUserFromCompany.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove user from company';
      })
      
      // Fetch company properties
      .addCase(fetchCompanyProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.companyProperties = action.payload;
        }
      })
      .addCase(fetchCompanyProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch company properties';
      })

      // Handle logout - reset all company state and clear cookie
      .addCase(logout.fulfilled, (state) => {
        state.companies = [];
        state.currentCompany = null;
        state.companyUsers = [];
        state.companyProperties = [];
        state.companyStats = null;
        state.pagination = initialState.pagination;
        state.filters = initialState.filters;
        state.userFilters = initialState.userFilters;
        state.isLoading = false;
        state.isLoadingCompanies = false;
        state.companiesLoaded = false;
        state.isCreating = false;
        state.isUpdating = false;
        state.isDeleting = false;
        state.error = null;
        // Clear the company cookie on logout
        cookieService.delete('currentCompanyId');
      });
  }
});

export const {
  setFilters,
  setUserFilters,
  clearError,
  resetCompanies,
  setCurrentCompany
} = companiesSlice.actions;

export default companiesSlice.reducer;